import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Button, message, Modal, Slider, InputNumber, Input } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageContainer, StyledCard } from './styles/SubscribePage.styles';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

const { Title, Text, Paragraph } = Typography;

interface SubscriptionPlan {
    id: number;
    name: string;
    description: string | null;
    price: number;
    durationDays: number | null;
    maxTournaments: number | null;
    maxParticipants: number | null;
    isConfigurable?: boolean;
    pricePerTournament?: number | null;
    pricePerParticipant?: number | null;
    features: string[] | null;
    isActive: boolean;
    sortOrder: number;
    promoCodes?: { id: number; code: string }[];
}

const SubscribePage: React.FC = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [loading, setLoading] = useState(false);
    const [purchasing, setPurchasing] = useState<number | null>(null);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await apiService.get<ApiResponse<SubscriptionPlan[]>>(
                API_ENDPOINTS.SUBSCRIPTION_PLANS.ACTIVE
            );
            
            // Нормализуем данные - убеждаемся, что features всегда массив или null
            const normalizedPlans = (Array.isArray(response.data.data) ? response.data.data : []).map(plan => ({
                ...plan,
                features: (() => {
                    if (!plan.features) return null;
                    if (Array.isArray(plan.features)) return plan.features;
                    if (typeof plan.features === 'string') {
                        try {
                            const parsed = JSON.parse(plan.features);
                            return Array.isArray(parsed) ? parsed : null;
                        } catch (e) {
                            return null;
                        }
                    }
                    return null;
                })()
            }));
            
            setPlans(normalizedPlans);
        } catch (error) {
            logger.error('Error fetching subscription plans:', error);
            message.error('Ошибка при загрузке планов подписок');
            setPlans([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    const handlePurchase = async (planId: number, overrides?: { maxParticipants?: number }, promoCode?: string) => {
        try {
            setPurchasing(planId);

            const response = await apiService.post<ApiResponse<any>>(
                API_ENDPOINTS.SUBSCRIPTIONS.PURCHASE,
                {
                    subscriptionPlanId: planId,
                    autoRenew: false,
                    paymentMethod: 'manual',
                    ...(overrides?.maxParticipants != null && { maxParticipants: overrides.maxParticipants }),
                    ...(promoCode && promoCode.trim() && { promoCode: promoCode.trim() })
                }
            );

            const responseData = response.data?.data || response.data;

            // Ответ API (в т.ч. данные от ЮKassa: confirmationUrl, redirectUrl) — в консоль для отладки
            console.log('[Subscribe] API response (subscriptions/purchase):', {
                fullResponse: response.data,
                responseData,
                payment: responseData?.payment,
                confirmationUrl: responseData?.payment?.confirmationUrl ?? responseData?.redirectUrl
            });

            const confirmationUrl = responseData?.payment?.confirmationUrl || responseData?.redirectUrl;
            const hasPaymentFlow = !!responseData?.payment;

            // Есть ссылка на оплату — переходим на ЮKassa
            if (confirmationUrl) {
                message.info('Переход на страницу оплаты...');
                window.location.href = confirmationUrl;
                return;
            }

            // Ожидалась оплата (есть объект payment), но ссылки нет — подписка не должна считаться оформленной
            if (hasPaymentFlow) {
                message.error('Не удалось получить ссылку на оплату. Подписка не активирована. Обратитесь в поддержку.');
                return;
            }

            // Подписка создана сразу без оплаты: бэкенд может вернуть data: { subscription } или data = сама подписка (id, subscriptionPlanId, status...)
            const isSubscriptionResponse =
                responseData?.subscription ||
                (responseData?.id != null && responseData?.subscriptionPlanId != null);
            if (isSubscriptionResponse) {
                message.success('Подписка успешно оформлена!');
                setTimeout(() => navigate('/subscriptions', { replace: true }), 1500);
                return;
            }

            // Неожиданный формат ответа
            message.error('Неожиданный ответ сервера. Попробуйте ещё раз или обратитесь в поддержку.');
        } catch (error: any) {
            logger.error('Error purchasing subscription:', error);
            const isUnauthorized = error?.response?.status === 401 || error?.sessionExpired === true;
            if (isUnauthorized) {
                message.error('Сессия истекла. Войдите снова для оформления подписки.');
                window.location.href = '/login';
                return;
            }
            const errorMessage = error?.response?.data?.message || 'Ошибка при оформлении подписки';
            message.error(errorMessage);
        } finally {
            setPurchasing(null);
        }
    };

    const [purchaseModalPlan, setPurchaseModalPlan] = useState<SubscriptionPlan | null>(null);
    const [sliderParticipants, setSliderParticipants] = useState(50);
    const [promoCodeInput, setPromoCodeInput] = useState('');

    const showPurchaseModal = (plan: SubscriptionPlan) => {
        setSliderParticipants(plan.maxParticipants ?? 50);
        setPromoCodeInput('');
        setPurchaseModalPlan(plan);
    };

    const hasPromoCodes = (purchaseModalPlan?.promoCodes?.length ?? 0) > 0;

    const isConfigurable = purchaseModalPlan?.isConfigurable && purchaseModalPlan?.maxParticipants != null;
    const participantsMin = 1;
    const participantsMax = Math.max(participantsMin, purchaseModalPlan?.maxParticipants ?? 200);

    return (
        <PageContainer>
            <Title level={2}>Выбор плана подписки</Title>
            <Paragraph>
                Выберите подходящий план подписки для вашего клуба. Подписка дает доступ к расширенным функциям системы управления турнирами.
            </Paragraph>

            {loading ? (
                <StyledCard>
                    <div style={{ padding: '48px 0', textAlign: 'center' }}>
                        <Text>Загрузка планов подписок...</Text>
                    </div>
                </StyledCard>
            ) : plans.length === 0 ? (
                <StyledCard>
                    <div style={{ padding: '48px 0', textAlign: 'center' }}>
                        <Text>Нет доступных планов подписки</Text>
                    </div>
                </StyledCard>
            ) : (
                <Row gutter={[24, 24]}>
                    {plans.map((plan) => (
                        <Col xs={24} sm={12} lg={8} key={plan.id} style={{ display: 'flex' }}>
                            <StyledCard
                                hoverable
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                                styles={{
                                    body: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        padding: '24px'
                                    }
                                }}
                            >
                                {/* Заголовок и описание */}
                                <div style={{ flexShrink: 0, marginBottom: 16 }}>
                                    <Title level={4} style={{ marginBottom: 8 }}>
                                        {plan.name}
                                    </Title>
                                    {plan.description && (
                                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                                            {plan.description}
                                        </Paragraph>
                                    )}
                                </div>

                                {/* Контентный блок - растягивается и занимает доступное пространство */}
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                    <div style={{ marginBottom: 16, flexShrink: 0 }}>
                                        {plan.isConfigurable && (plan.pricePerTournament != null || plan.pricePerParticipant != null) ? (
                                            <>
                                                <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>
                                                    {plan.pricePerTournament != null && plan.pricePerTournament > 0 && (
                                                        <>{parseFloat(String(plan.pricePerTournament)).toFixed(2)} ₽/турнир</>
                                                    )}
                                                    {plan.pricePerTournament != null && plan.pricePerTournament > 0 && plan.pricePerParticipant != null && plan.pricePerParticipant > 0 && ' · '}
                                                    {plan.pricePerParticipant != null && plan.pricePerParticipant > 0 && (
                                                        <>{parseFloat(String(plan.pricePerParticipant)).toFixed(2)} ₽/участник</>
                                                    )}
                                                </Text>
                                                <br />
                                                <Text type="secondary">Цена считается при оформлении</Text>
                                            </>
                                        ) : (
                                            <>
                                                <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>
                                                    {parseFloat(plan.price.toString()).toFixed(2)} ₽
                                                </Text>
                                                {plan.durationDays !== null && (
                                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                                        / {plan.durationDays} дней
                                                    </Text>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div style={{ marginBottom: 16, flexShrink: 0 }}>
                                        {plan.durationDays !== null && (
                                            <>
                                                <Text strong>Длительность: </Text>
                                                <Text>{plan.durationDays} дней</Text>
                                                <br />
                                            </>
                                        )}
                                        {plan.maxTournaments != null && (
                                            <>
                                                <Text strong>Лимит создания турниров: </Text>
                                                <Text>{plan.maxTournaments}</Text>
                                                <br />
                                            </>
                                        )}
                                        {plan.maxParticipants != null && (
                                            <>
                                                <Text strong>Лимит регистраций участников: </Text>
                                                <Text>{plan.maxParticipants}</Text>
                                                <br />
                                            </>
                                        )}
                                        {plan.isConfigurable && plan.maxParticipants != null && (
                                            <>
                                                <br />
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Настраиваемый тариф — выберите количество участников при оформлении</Text>
                                            </>
                                        )}
                                    </div>

                                    {/* Блок с возможностями - занимает оставшееся пространство */}
                                    {plan.features && Array.isArray(plan.features) && plan.features.length > 0 ? (
                                        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                            <Text strong>Возможности:</Text>
                                            <ul style={{ marginTop: 8, paddingLeft: 20, marginBottom: 0 }}>
                                                {plan.features.map((feature, index) => (
                                                    <li key={index} style={{ marginBottom: 4 }}>
                                                        <Text>{feature}</Text>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : (
                                        <div style={{ flex: 1, minHeight: 0 }} />
                                    )}
                                </div>

                                {/* Кнопка - всегда внизу */}
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ShoppingCartOutlined />}
                                    block
                                    loading={purchasing === plan.id}
                                    onClick={() => showPurchaseModal(plan)}
                                    style={{ flexShrink: 0, marginTop: 16 }}
                                >
                                    Оформить подписку
                                </Button>
                            </StyledCard>
                        </Col>
                    ))}
                </Row>
            )}

            <Modal
                title={purchaseModalPlan ? `Оформление подписки: ${purchaseModalPlan.name}` : 'Оформление подписки'}
                open={!!purchaseModalPlan}
                onCancel={() => setPurchaseModalPlan(null)}
                onOk={async () => {
                    if (!purchaseModalPlan) return;
                    const overrides = isConfigurable
                        ? { ...(purchaseModalPlan.maxParticipants != null && { maxParticipants: sliderParticipants }) }
                        : undefined;
                    await handlePurchase(purchaseModalPlan.id, overrides, hasPromoCodes ? promoCodeInput : undefined);
                    setPurchaseModalPlan(null);
                }}
                okText="Оформить подписку"
                cancelText="Отмена"
                width={520}
                confirmLoading={purchasing === purchaseModalPlan?.id}
            >
                {purchaseModalPlan && (
                    <div style={{ marginTop: 16 }}>
                        {isConfigurable && purchaseModalPlan.pricePerParticipant != null ? (
                            <>
                                <Paragraph style={{ marginBottom: 16 }}>
                                    <strong>Тариф:</strong> {parseFloat(String(purchaseModalPlan.pricePerParticipant)).toFixed(2)} ₽/участник
                                </Paragraph>
                                {purchaseModalPlan.maxParticipants != null && (
                                    <div style={{ marginBottom: 20 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                            <Text strong>Количество участников</Text>
                                            <InputNumber
                                                min={participantsMin}
                                                max={participantsMax}
                                                value={sliderParticipants}
                                                onChange={(v) => {
                                                    const n = v == null ? participantsMin : Math.min(participantsMax, Math.max(participantsMin, Number(v)));
                                                    setSliderParticipants(Number.isFinite(n) ? n : sliderParticipants);
                                                }}
                                                style={{ width: 90 }}
                                                controls
                                            />
                                            <Text type="secondary">(от {participantsMin} до {participantsMax})</Text>
                                        </div>
                                        <Slider
                                            min={participantsMin}
                                            max={participantsMax}
                                            value={sliderParticipants}
                                            onChange={setSliderParticipants}
                                            marks={{
                                                [participantsMin]: String(participantsMin),
                                                [participantsMax]: String(participantsMax)
                                            }}
                                            tooltip={{ formatter: (v) => `${v} уч.` }}
                                        />
                                    </div>
                                )}
                                <div style={{ padding: '12px 16px', background: '#f5f7fa', borderRadius: 8, marginBottom: 16 }}>
                                    <Text strong>Расчёт: </Text>
                                    {purchaseModalPlan.pricePerParticipant != null && purchaseModalPlan.pricePerParticipant > 0 && (
                                        <Text>{sliderParticipants} уч. × {parseFloat(String(purchaseModalPlan.pricePerParticipant)).toFixed(2)} ₽</Text>
                                    )}
                                    <br />
                                    <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                                        Итого: {(sliderParticipants * (purchaseModalPlan.pricePerParticipant != null ? parseFloat(String(purchaseModalPlan.pricePerParticipant)) : 0)).toFixed(2)} ₽
                                    </Text>
                                </div>
                            </>
                        ) : (
                            <>
                                <Paragraph>
                                    <strong>Цена:</strong> {parseFloat(purchaseModalPlan.price.toString()).toFixed(2)} ₽
                                </Paragraph>
                                {purchaseModalPlan.durationDays !== null && (
                                    <Paragraph>
                                        <strong>Длительность:</strong> {purchaseModalPlan.durationDays} дней
                                    </Paragraph>
                                )}
                                {purchaseModalPlan.durationDays === null && (
                                    <Paragraph>
                                        <strong>Длительность:</strong> Без ограничения по времени
                                    </Paragraph>
                                )}
                                {purchaseModalPlan.maxParticipants != null && (
                                    <Paragraph>
                                        <strong>Лимит участников:</strong> {purchaseModalPlan.maxParticipants}
                                    </Paragraph>
                                )}
                            </>
                        )}
                        {!isConfigurable && purchaseModalPlan.durationDays !== null && (
                            <Paragraph>
                                <strong>Длительность:</strong> {purchaseModalPlan.durationDays} дней
                            </Paragraph>
                        )}
                        {isConfigurable && purchaseModalPlan.durationDays != null && (
                            <Paragraph>
                                <strong>Длительность подписки:</strong> {purchaseModalPlan.durationDays} дней
                            </Paragraph>
                        )}
                        {hasPromoCodes && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>Промокод на скидку</Text>
                                <Input
                                    placeholder="Введите промокод"
                                    value={promoCodeInput}
                                    onChange={(e) => setPromoCodeInput(e.target.value)}
                                    style={{ marginTop: 6 }}
                                    allowClear
                                />
                            </div>
                        )}
                        {purchaseModalPlan.description && (
                            <Paragraph style={{ marginBottom: 0 }}>
                                <strong>Описание:</strong> {purchaseModalPlan.description}
                            </Paragraph>
                        )}
                    </div>
                )}
            </Modal>
        </PageContainer>
    );
};

export default SubscribePage;
